import { Field } from "src/shared/contexts/HackerContext/types";
import { Nullable } from "src/shared/utils/typescript";

export type PersonalPrefillAnswers = {
  [Field.LEGAL_NAME]: Nullable<string>;
  [Field.SHORT_BIRTHDATE]: Nullable<string>;
  [Field.PREFERRED_NAME]: Nullable<string>;
  [Field.PRONOUNS]: Nullable<string[]>;
  [Field.PHONE_NUMBER]: Nullable<string>;
};

export type PersonalPrefillSources = {
  applicationName?: Nullable<string>;
  accountName?: Nullable<string>;
  accountLegalName?: Nullable<string>;
  accountBirthDate?: Nullable<string>;
  accountPhone?: Nullable<string>;
};

const nonEmptyString = (value: Nullable<string> | undefined): string | null => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
};

const existingStringOr = (
  current: Nullable<string>,
  ...fallbacks: Array<Nullable<string> | undefined>
): Nullable<string> => {
  if (nonEmptyString(current)) return current;

  return (
    fallbacks.map(nonEmptyString).find((value) => value != null) ?? current
  );
};

/** Convert the user table's ISO datetime into the date input's YYYY-MM-DD. */
const dateOnly = (value: Nullable<string> | undefined): string | null => {
  const normalized = nonEmptyString(value);
  if (!normalized) return null;

  const match = normalized.match(/^(\d{4}-\d{2}-\d{2})/);
  return match?.[1] ?? null;
};

/**
 * Fill only unanswered RSVP personal fields from application/account data.
 * Existing RSVP answers always win, including the application birthdate that
 * already shares Field.SHORT_BIRTHDATE with this form.
 */
export const buildPersonalPrefillValues = (
  current: PersonalPrefillAnswers,
  sources: PersonalPrefillSources
): PersonalPrefillAnswers => {
  const legalName = existingStringOr(
    current[Field.LEGAL_NAME],
    sources.accountLegalName,
    sources.applicationName,
    sources.accountName
  );

  const accountName = nonEmptyString(sources.accountName);
  const preferredNameFallback =
    accountName && accountName !== nonEmptyString(legalName)
      ? accountName
      : null;

  return {
    [Field.LEGAL_NAME]: legalName,
    [Field.SHORT_BIRTHDATE]:
      nonEmptyString(current[Field.SHORT_BIRTHDATE]) != null
        ? current[Field.SHORT_BIRTHDATE]
        : dateOnly(sources.accountBirthDate) ?? current[Field.SHORT_BIRTHDATE],
    [Field.PREFERRED_NAME]: existingStringOr(
      current[Field.PREFERRED_NAME],
      preferredNameFallback
    ),
    [Field.PRONOUNS]: current[Field.PRONOUNS],
    [Field.PHONE_NUMBER]: existingStringOr(
      current[Field.PHONE_NUMBER],
      sources.accountPhone
    ),
  };
};
